import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Calendar, ChevronRight, ChevronLeft } from 'lucide-react';
import { BankAccount, AccountingCategory, FrequencyType, FrequencyConfig, EndCondition, EndConditionType, OffsetCode } from '../../types/database';
import { RecurringActionLineInsert } from '../../lib/recurringActionsService';
import { offsetCodesService } from '../../lib/offsetCodesService';
import { emitToast } from '../ui/Toast';
import { formatDateSafe } from '../../lib/dateUtils';

/**
 * Returns today's date as a "YYYY-MM-DD" string using LOCAL time,
 * never UTC — avoids the midnight-UTC offset shift on Hebrew/IST browsers.
 */
function localTodayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Extracts a "YYYY-MM-DD" string from a LOCAL Date object without
 * converting through UTC (avoids the -1 day shift for UTC+ zones).
 */
function localDateToISO(d: Date): string {
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Optional pre-fill values injected by the Discovery Wizard */
export interface RecurringPrefillData {
  description: string;
  amount: number;
  start_date: string; // YYYY-MM-DD
}

interface AddRecurringActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (lines: RecurringActionLineInsert[]) => void;
  bankAccounts: BankAccount[];
  accountingCategories: AccountingCategory[];
  isSaving: boolean;
  companyId: string;
  /** When provided, the form opens pre-populated with these values */
  prefillData?: RecurringPrefillData | null;
}

interface RecurringActionFormData {
  description: string;
  amount: string;
  bank_account_id: string;
  accounting_category_id: string;
  offset_code_id: string;
  start_date: string;
  frequency_config: FrequencyConfig;
  end_condition: EndCondition;
}

interface GeneratedLine {
  action_date: string;
  description: string;
  amount: number;
  bank_account_id: string;
  accounting_category_id: string;
  offset_code_id?: string | null;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'ראשון' },
  { value: 1, label: 'שני' },
  { value: 2, label: 'שלישי' },
  { value: 3, label: 'רביעי' },
  { value: 4, label: 'חמישי' },
  { value: 5, label: 'שישי' },
  { value: 6, label: 'שבת' },
];

export function AddRecurringActionModal({
  isOpen,
  onClose,
  onSave,
  bankAccounts,
  accountingCategories,
  isSaving,
  companyId,
  prefillData,
}: AddRecurringActionModalProps) {
  const [step, setStep] = useState<'form' | 'preview'>('form');
  const [generatedLines, setGeneratedLines] = useState<GeneratedLine[]>([]);
  const [offsetCodes, setOffsetCodes] = useState<OffsetCode[]>([]);
  const [loadingOffsets, setLoadingOffsets] = useState(false);
  
  const [formData, setFormData] = useState<RecurringActionFormData>({
    description: prefillData?.description ?? '',
    amount:      prefillData != null ? String(prefillData.amount) : '',
    bank_account_id: '',
    accounting_category_id: '',
    offset_code_id: '',
    start_date: prefillData?.start_date ?? localTodayISO(),
    frequency_config: {
      type: 'monthly',
      daysOfMonth: [1],
    },
    end_condition: {
      type: 'after_occurrences',
      occurrences: 12,
    },
  });

  // Helper function defined before useEffect
  const loadOffsetCodes = async () => {
    setLoadingOffsets(true);
    try {
      const { data, error } = await offsetCodesService.getOffsetCodes(companyId);
      if (error) {
        console.error('Error loading offset codes:', error);
      } else if (data) {
        // Filter only active offset codes
        setOffsetCodes(data.filter(code => code.is_active));
      }
    } catch (err) {
      console.error('Error loading offset codes:', err);
    } finally {
      setLoadingOffsets(false);
    }
  };

  // ── resetForm MUST be declared before useEffect (and before the early return)
  //    so the reference is never in the Temporal Dead Zone when effects fire.
  const resetForm = useCallback(() => {
    setFormData({
      description: '',
      amount: '',
      // Re-apply the first bank account default so the form is ready immediately
      bank_account_id: bankAccounts[0]?.id ?? '',
      accounting_category_id: '',
      offset_code_id: '',
      start_date: localTodayISO(),        // local date — no UTC shift
      frequency_config: {
        type: 'monthly',
        daysOfMonth: [1],
      },
      end_condition: {
        type: 'after_occurrences',
        occurrences: 12,
      },
    });
    setStep('form');
    setGeneratedLines([]);
  }, [bankAccounts]);

  // Fetch offset codes when modal opens; also apply prefillData when it changes
  useEffect(() => {
    if (isOpen && companyId) {
      loadOffsetCodes();
    }
  }, [isOpen, companyId]);

  // When prefillData changes (new suggestion approved), update the form fields
  useEffect(() => {
    if (prefillData) {
      setFormData(prev => ({
        ...prev,
        description: prefillData.description,
        amount:      String(prefillData.amount),
        start_date:  prefillData.start_date,
      }));
      setStep('form');
      setGeneratedLines([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData]);

  // Default bank account — set to the first account as soon as the list is
  // available and the field hasn't been filled in yet by the user.
  useEffect(() => {
    if (bankAccounts.length > 0 && !formData.bank_account_id) {
      setFormData(prev => ({
        ...prev,
        bank_account_id: bankAccounts[0]!.id,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccounts]);

  if (!isOpen) return null;

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const generateDates = (): Date[] => {
    const dates: Date[] = [];

    // Parse startDate from the ISO string directly so we never get a UTC-midnight
    // timezone shift.  We build a LOCAL midnight date by splitting "YYYY-MM-DD".
    const [sy, sm, sd] = formData.start_date.split('-').map(Number);
    // startDay: local midnight of the user-chosen start date
    const startDay = new Date(sy!, (sm! - 1), sd!);
    startDay.setHours(0, 0, 0, 0);

    const maxOccurrences =
      formData.end_condition.type === 'after_occurrences'
        ? (formData.end_condition.occurrences || 1)
        : 1000;

    const endDate =
      formData.end_condition.type === 'specific_date' && formData.end_condition.specificDate
        ? (() => {
            const [ey, em, ed] = formData.end_condition.specificDate.split('-').map(Number);
            const d = new Date(ey!, (em! - 1), ed!);
            d.setHours(23, 59, 59, 999); // inclusive end
            return d;
          })()
        : null;

    // ── Passthrough modes (user picks explicit dates) ──────────────────────
    if (formData.frequency_config.type === 'custom' && formData.frequency_config.customDates) {
      return formData.frequency_config.customDates
        .filter((s): s is string => !!s && s.trim() !== '')
        .map(s => new Date(s))
        .filter(d => !isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    }

    if (formData.frequency_config.type === 'quarterly' && formData.frequency_config.quarterlyDates) {
      return formData.frequency_config.quarterlyDates
        .filter((s): s is string => !!s && s.trim() !== '')
        .map(s => new Date(s))
        .filter(d => !isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    }

    if (formData.frequency_config.type === 'semi_annually' && formData.frequency_config.semiAnnuallyDates) {
      return formData.frequency_config.semiAnnuallyDates
        .filter((s): s is string => !!s && s.trim() !== '')
        .map(s => new Date(s))
        .filter(d => !isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    }

    if (formData.frequency_config.type === 'annually' && formData.frequency_config.annuallyDate) {
      return [new Date(formData.frequency_config.annuallyDate)];
    }

    // ── Rule-based modes ───────────────────────────────────────────────────
    //
    // KEY FIX: we do NOT blindly push startDate as the first occurrence.
    // Instead we "seek" currentDate forward until it lands on the first date
    // that satisfies the recurring rule AND is >= startDate.
    // Only after finding that anchor do we enter the push→advance loop.

    let currentDate = new Date(startDay); // mutable cursor

    switch (formData.frequency_config.type) {
      // ── daily: every calendar day from startDate (startDate is valid) ────
      case 'daily':
        // startDate itself is always valid for daily — no seeking needed.
        break;

      // ── weekly_5: every weekday (Mon–Fri) ─────────────────────────────
      case 'weekly_5':
        // Advance until we land on a weekday
        while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
          currentDate.setDate(currentDate.getDate() + 1);
        }
        break;

      // ── weekly_6: every day except Sunday ─────────────────────────────
      case 'weekly_6':
        // Advance past Sunday if startDate falls on one
        if (currentDate.getDay() === 0) {
          currentDate.setDate(currentDate.getDate() + 1);
        }
        break;

      // ── weekly: specific day of week ────────────────────────────────────
      case 'weekly': {
        const targetDow = formData.frequency_config.dayOfWeek ?? 1; // default Monday
        while (currentDate.getDay() !== targetDow) {
          currentDate.setDate(currentDate.getDate() + 1);
        }
        break;
      }

      // ── monthly: specific day(s) of month ───────────────────────────────
      case 'monthly': {
        const sortedDays = formData.frequency_config.daysOfMonth?.length
          ? [...formData.frequency_config.daysOfMonth].sort((a, b) => a - b)
          : null;

        if (sortedDays) {
          // Find the first configured day-of-month that is >= startDate's day
          // within the same month.  If none exist, roll to the next month.
          const startDayOfMonth = currentDate.getDate();
          const nextDayInSameMonth = sortedDays.find(d => d >= startDayOfMonth);

          if (nextDayInSameMonth !== undefined) {
            // First valid occurrence is in the same month as startDate
            currentDate.setDate(nextDayInSameMonth);
          } else {
            // No configured day is >= startDay in this month → use first day
            // of next month
            currentDate.setMonth(currentDate.getMonth() + 1);
            currentDate.setDate(sortedDays[0]!);
          }
        }
        // If no daysOfMonth configured, leave currentDate as-is (same-day monthly)
        break;
      }

      // ── quarterly / semi_annually / annually: advance from startDate ─────
      case 'quarterly':
      case 'semi_annually':
      case 'annually':
        // startDate is the anchor — these modes don't have a "specific day"
        // constraint so startDate itself is always the first valid occurrence.
        break;
    }

    // ── Main generation loop (push current → advance → repeat) ────────────
    let occurrenceCount = 0;

    while (occurrenceCount < maxOccurrences) {
      if (endDate && currentDate > endDate) break;

      dates.push(new Date(currentDate));
      occurrenceCount++;

      // Advance to the NEXT occurrence after the one just pushed
      switch (formData.frequency_config.type) {
        case 'daily':
          currentDate.setDate(currentDate.getDate() + 1);
          break;

        case 'weekly_5':
          // Move forward at least one day, then skip weekends
          do {
            currentDate.setDate(currentDate.getDate() + 1);
          } while (currentDate.getDay() === 0 || currentDate.getDay() === 6);
          break;

        case 'weekly_6':
          currentDate.setDate(currentDate.getDate() + 1);
          if (currentDate.getDay() === 0) {
            currentDate.setDate(currentDate.getDate() + 1);
          }
          break;

        case 'weekly':
          currentDate.setDate(currentDate.getDate() + 7);
          break;

        case 'monthly': {
          const sortedDays = formData.frequency_config.daysOfMonth?.length
            ? [...formData.frequency_config.daysOfMonth].sort((a, b) => a - b)
            : null;

          if (sortedDays) {
            // Find the next configured day that is strictly AFTER the current day
            const thisDayOfMonth = currentDate.getDate();
            const nextDayInSameMonth = sortedDays.find(d => d > thisDayOfMonth);

            if (nextDayInSameMonth !== undefined) {
              currentDate.setDate(nextDayInSameMonth);
            } else {
              // Wrap to next month, first configured day
              currentDate.setMonth(currentDate.getMonth() + 1);
              currentDate.setDate(sortedDays[0]!);
            }
          } else {
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
          break;
        }

        case 'quarterly':
          currentDate.setMonth(currentDate.getMonth() + 3);
          break;

        case 'semi_annually':
          currentDate.setMonth(currentDate.getMonth() + 6);
          break;

        case 'annually':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
      }
    }

    return dates;
  };

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required accounting category
    if (!formData.accounting_category_id) {
      emitToast('חובה לבחור סיווג הנהלת חשבונות', 'error');
      return;
    }
    
    const dates = generateDates();
    const amount = parseFloat(formData.amount);
    
    const lines: GeneratedLine[] = dates.map(date => {
      // Use local-time extraction — toISOString() converts to UTC which
      // shifts dates like Jan 1 midnight local → Dec 31 UTC in UTC+ zones.
      const dateStr = localDateToISO(date);

      return {
        action_date: dateStr,
        description: formData.description,
        amount: amount,
        bank_account_id: formData.bank_account_id,
        accounting_category_id: formData.accounting_category_id,
        offset_code_id: formData.offset_code_id || null,
      };
    });

    setGeneratedLines(lines);
    setStep('preview');
  };

  const handleConfirmSave = () => {
    const linesToInsert: RecurringActionLineInsert[] = generatedLines.map(line => ({
      ...line,
      company_id: companyId,
    }));
    
    onSave(linesToInsert);
  };

  const updateFrequencyType = (type: FrequencyType) => {
    const newConfig: FrequencyConfig = { type };
    
    switch (type) {
      case 'weekly':
        newConfig.dayOfWeek = 1;
        break;
      case 'monthly':
        newConfig.daysOfMonth = [1];
        break;
      case 'quarterly':
        newConfig.quarterlyDates = ['', '', '', ''];
        break;
      case 'semi_annually':
        newConfig.semiAnnuallyDates = ['', ''];
        break;
      case 'annually':
        newConfig.annuallyDate = '';
        break;
      case 'custom':
        newConfig.customDates = [''];
        break;
    }
    
    setFormData({ ...formData, frequency_config: newConfig });
  };

  const toggleDayOfMonth = (day: number) => {
    const currentDays = formData.frequency_config.daysOfMonth || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort((a, b) => a - b);
    
    setFormData({
      ...formData,
      frequency_config: { ...formData.frequency_config, daysOfMonth: newDays },
    });
  };

  const updateQuarterlyDate = (index: number, value: string) => {
    const dates = [...(formData.frequency_config.quarterlyDates || ['', '', '', ''])];
    dates[index] = value;
    setFormData({
      ...formData,
      frequency_config: { ...formData.frequency_config, quarterlyDates: dates },
    });
  };

  const updateSemiAnnuallyDate = (index: number, value: string) => {
    const dates = [...(formData.frequency_config.semiAnnuallyDates || ['', ''])];
    dates[index] = value;
    setFormData({
      ...formData,
      frequency_config: { ...formData.frequency_config, semiAnnuallyDates: dates },
    });
  };

  const updateCustomDate = (index: number, value: string) => {
    const dates = [...(formData.frequency_config.customDates || [''])];
    dates[index] = value;
    setFormData({
      ...formData,
      frequency_config: { ...formData.frequency_config, customDates: dates },
    });
  };

  const addCustomDate = () => {
    const dates = [...(formData.frequency_config.customDates || []), ''];
    setFormData({
      ...formData,
      frequency_config: { ...formData.frequency_config, customDates: dates },
    });
  };

  const removeCustomDate = (index: number) => {
    const dates = (formData.frequency_config.customDates || []).filter((_, i) => i !== index);
    setFormData({
      ...formData,
      frequency_config: { ...formData.frequency_config, customDates: dates },
    });
  };

  const totalAmount = generatedLines.reduce((sum, line) => sum + line.amount, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {step === 'form' ? 'הוסף פעולות קבועות' : 'תצוגה מקדימה'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSaving}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {step === 'form' ? (
          /* FORM STEP */
          <form onSubmit={handleGenerate}>
            <div className="p-6 space-y-6">
              {/* Base Fields */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  פרטים בסיסיים
                </h3>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    תאריך התחלה <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    תיאור <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    placeholder="לדוגמה: שכר דירה חודשי"
                    required
                    dir="rtl"
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    סכום <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    placeholder="0.00"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500 text-right">
                    מספר חיובי להכנסה, שלילי להוצאה
                  </p>
                </div>

                {/* Bank Account */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    חשבון בנק <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.bank_account_id}
                    onChange={(e) => setFormData({ ...formData, bank_account_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    required
                  >
                    <option value="">בחר חשבון בנק</option>
                    {bankAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.nickname} - {account.bank_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Accounting Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    סיווג הנהלת חשבונות <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.accounting_category_id}
                    onChange={(e) => setFormData({ ...formData, accounting_category_id: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right ${
                      !formData.accounting_category_id ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">בחר סיווג</option>
                    {accountingCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Offset Rule */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    חוק קיזוז
                  </label>
                  <select
                    value={formData.offset_code_id}
                    onChange={(e) => setFormData({ ...formData, offset_code_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    disabled={loadingOffsets}
                  >
                    <option value="">ללא קיזוז</option>
                    {offsetCodes.map((code) => (
                      <option key={code.id} value={code.id}>
                        {code.code_number} - {code.name}
                      </option>
                    ))}
                  </select>
                  {loadingOffsets && (
                    <p className="mt-1 text-xs text-gray-500 text-right">
                      טוען חוקי קיזוז...
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 text-right">
                    אופציונלי - בחר חוק קיזוז לקישור אוטומטי בין פעולות צפויות לפועל
                  </p>
                </div>
              </div>

              {/* Frequency Block */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  תדירות
                </h3>

                {/* Frequency Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    סוג תדירות <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.frequency_config.type}
                    onChange={(e) => updateFrequencyType(e.target.value as FrequencyType)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    required
                  >
                    <option value="daily">יומי</option>
                    <option value="weekly_5">שבועי (5 ימים)</option>
                    <option value="weekly_6">שבועי (6 ימים)</option>
                    <option value="weekly">שבועי</option>
                    <option value="monthly">חודשי</option>
                    <option value="quarterly">רבעוני</option>
                    <option value="semi_annually">חצי שנתי</option>
                    <option value="annually">שנתי</option>
                    <option value="custom">תאריכים מותאמים אישית</option>
                  </select>
                </div>

                {/* Conditional Frequency Fields */}
                {formData.frequency_config.type === 'weekly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                      יום בשבוע <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.frequency_config.dayOfWeek ?? 1}
                      onChange={(e) => setFormData({
                        ...formData,
                        frequency_config: { ...formData.frequency_config, dayOfWeek: parseInt(e.target.value) }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                      required
                    >
                      {DAYS_OF_WEEK.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.frequency_config.type === 'monthly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                      ימים בחודש <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleDayOfMonth(day)}
                          className={`
                            px-3 py-2 rounded-lg text-sm font-medium transition-colors
                            ${(formData.frequency_config.daysOfMonth || []).includes(day)
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }
                          `}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {formData.frequency_config.type === 'quarterly' && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 text-right">
                      4 תאריכים רבעוניים <span className="text-red-500">*</span>
                    </label>
                    {[0, 1, 2, 3].map((index) => (
                      <div key={index}>
                        <label className="block text-xs text-gray-600 mb-1 text-right">
                          רבעון {index + 1}
                        </label>
                        <input
                          type="date"
                          value={(formData.frequency_config.quarterlyDates || [])[index] || ''}
                          onChange={(e) => updateQuarterlyDate(index, e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                    ))}
                  </div>
                )}

                {formData.frequency_config.type === 'semi_annually' && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 text-right">
                      2 תאריכים חצי שנתיים <span className="text-red-500">*</span>
                    </label>
                    {[0, 1].map((index) => (
                      <div key={index}>
                        <label className="block text-xs text-gray-600 mb-1 text-right">
                          חצי שנה {index + 1}
                        </label>
                        <input
                          type="date"
                          value={(formData.frequency_config.semiAnnuallyDates || [])[index] || ''}
                          onChange={(e) => updateSemiAnnuallyDate(index, e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                    ))}
                  </div>
                )}

                {formData.frequency_config.type === 'annually' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                      תאריך שנתי <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.frequency_config.annuallyDate || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        frequency_config: { ...formData.frequency_config, annuallyDate: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                )}

                {formData.frequency_config.type === 'custom' && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 text-right">
                      תאריכים מותאמים אישית <span className="text-red-500">*</span>
                    </label>
                    {(formData.frequency_config.customDates || ['']).map((date, index) => (
                      <div key={index} className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => removeCustomDate(index)}
                          className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                          disabled={(formData.frequency_config.customDates || []).length <= 1}
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <input
                          type="date"
                          value={date}
                          onChange={(e) => updateCustomDate(index, e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addCustomDate}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      הוסף תאריך
                    </button>
                  </div>
                )}
              </div>

              {/* End Condition Block */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  תנאי סיום
                </h3>

                {/* End Condition Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    סוג תנאי סיום <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.end_condition.type}
                    onChange={(e) => setFormData({
                      ...formData,
                      end_condition: { type: e.target.value as EndConditionType }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    required
                  >
                    <option value="specific_date">תאריך ספציפי</option>
                    <option value="after_occurrences">אחרי X פעמים</option>
                  </select>
                </div>

                {/* Conditional End Condition Fields */}
                {formData.end_condition.type === 'specific_date' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                      תאריך סיום <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.end_condition.specificDate || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        end_condition: { ...formData.end_condition, specificDate: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                )}

                {formData.end_condition.type === 'after_occurrences' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                      מספר פעמים <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.end_condition.occurrences || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        end_condition: { ...formData.end_condition, occurrences: parseInt(e.target.value) }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                      placeholder="1"
                      required
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ביטול
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                חשב ותצוגה מקדימה
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </form>
        ) : (
          /* PREVIEW STEP */
          <div>
            <div className="p-6 space-y-6">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  מוכן ליצירת {generatedLines.length} שורות
                </h3>
                <p className="text-blue-800">
                  סכום כולל: <span className="font-bold">{totalAmount.toFixed(2)} ₪</span>
                </p>
              </div>

              {/* Dates List */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">תאריכים שיווצרו:</h4>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">#</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">תאריך</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">סכום</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {generatedLines.map((line, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{index + 1}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">
                            {/* formatDateSafe splits the ISO string directly — no UTC conversion */}
                            {formatDateSafe(line.action_date)}
                          </td>
                          <td className={`px-4 py-2 text-sm font-medium text-right ${
                            line.amount >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {line.amount.toFixed(2)} ₪
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                type="button"
                onClick={() => setStep('form')}
                className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                disabled={isSaving}
              >
                <ChevronRight className="w-4 h-4" />
                חזור לעריכה
              </button>
              <button
                type="button"
                onClick={handleConfirmSave}
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    שומר...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4" />
                    אשר ושמור ב-DB
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
